'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { Button } from './Button';

interface UnsavedChangesModalProps {
    isOpen: boolean;
    onSave: () => void;
    onDiscard: () => void;
    onCancel: () => void;
    title?: string;
    description?: string;
    saveLabel?: string;
    discardLabel?: string;
}

export function UnsavedChangesModal({ isOpen, onSave, onDiscard, onCancel, title, description, saveLabel, discardLabel }: UnsavedChangesModalProps) {
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    if (!isClient) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onCancel}
                    />

                    {/* Modal */}
                    <motion.div
                        className="relative bg-white/80 backdrop-blur-md shadow-2xl p-8 w-full max-w-lg border border-white/20 rounded-none"
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    >
                        <h3 className="text-xl font-bold text-black mb-3">{title ?? 'Unsaved Changes'}</h3>
                        <p className="text-slate-600 mb-8 text-base">
                            {description ?? 'Do you want to save your changes before leaving?'}
                        </p>

                        <div className="flex items-center justify-end gap-3">
                            <Button
                                onClick={onDiscard}
                                variant="outline"
                                className="rounded-none whitespace-nowrap"
                            >
                                {discardLabel ?? 'No'}
                            </Button>
                            <Button
                                onClick={onSave}
                                variant="primary"
                                className="rounded-none whitespace-nowrap"
                            >
                                {saveLabel ?? 'Yes'}
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
}
